import { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import {
  getAllAttendances,
  getAllBeneficiaries
} from '../../../API/apiService';
import {
  formatDateInBogota,
  formatIsoDateInBogota,
  getCurrentIsoDateInBogota
} from '../../../utils/dateTime';
import { isGroupCancelled, resolveGroupStatus } from '../../../utils/groupStatus';

const PROCESS_SHEET_HEADERS = [
  'CODIGO PROCESO FORMATIVO',
  'TIPO DE PROCESO FORMATIVO',
  'GRUPO',
  'ESTADO',
  'FECHA DE FINALIZACION',
  'MEDIADOR',
  'COMUNA',
  'BARRIO',
  'ORGANIZACION MADRINA',
  'NOMBRE INSTITUCION EDUCATIVA'
];

const PROCESS_SHEET_HEADERS_WITHOUT_FINALIZATION = PROCESS_SHEET_HEADERS.filter(
  header => header !== 'FECHA DE FINALIZACION'
);

const ADVISORY_SHEET_HEADERS = [
  'CODIGO ASESORIA',
  'TIPO DE ASESORIA',
  'MEDIADOR',
  'MEDIADORES ADICIONALES',
  'COMUNA',
  'BARRIO',
  'DEPENDENCIA',
  'NOMBRE ASESORIA'
];

const FIRST_SESSION_HEADERS = [
  'CODIGO PROCESO FORMATIVO',
  'GRUPO',
  'FECHA PRIMERA SESION'
];

const SOCIAL_CONTROL_ORGANIZATION_HEADERS = [
  'Comuna',
  'Tipo de Organización',
  'Nombre de la organización',
  'Actividad en la que Participó',
  'Fecha en la que participó',
  'Mes del reporte'
];

const SCHOOLS_BY_MUNICIPALITY_HEADERS = [
  'COMUNA',
  'NOMBRE INSTITUCION EDUCATIVA',
  'BARRIO'
];

const ADVISORIES_BY_MUNICIPALITY_HEADERS = [
  'COMUNA',
  'CODIGO ASESORIA',
  'NOMBRE ASESORIA',
  'BARRIO',
  'DEPENDENCIA',
  'MEDIADOR',
  'MEDIADORES ADICIONALES',
  'TIPOS DE ASESORIA'
];

const COP_ADVISORY_TYPE_LABELS = {
  DEPEN: 'Asesoría',
  ASESORIA: 'Asesoría',
  COMITE_PEDAGOGICO: 'Comité Pedagógico',
  EVENTO: 'Evento',
  OTRO: 'Otro'
};

function normalizeText(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

function toNumber(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function normalizeMeetingType(value) {
  return String(value || '').trim().toUpperCase();
}

function sortTextValues(values) {
  return [...values].sort((left, right) =>
    String(left || '').localeCompare(String(right || ''), 'es', {
      numeric: true,
      sensitivity: 'base'
    })
  );
}

function getUserDisplayName(user) {
  return String(user?.full_name ?? user?.fullName ?? user?.name ?? '').trim();
}

function getAdditionalMediators(row) {
  return String(row?.additionalMediators ?? row?.additional_mediators ?? '').trim();
}

function getGroupTypeText(group) {
  const ageRange = String(group?.age_range ?? group?.ageRange ?? '').trim();
  if (ageRange) return ageRange;

  const legacyDescription = String(group?.description ?? '').trim();
  const normalizedLegacyDescription = normalizeText(legacyDescription);
  return ['docente', 'comunidad', 'estudiante'].some(type =>
    normalizedLegacyDescription.includes(type)
  )
    ? legacyDescription
    : '';
}

function resolveFormativeProcessType(group) {
  const groupType = normalizeText(getGroupTypeText(group));
  if (groupType.includes('docente')) return 'Docentes';
  if (groupType.includes('comunidad')) return 'Comunidad';
  if (groupType.includes('estudiante')) return 'Estudiantes';

  const code = String(group?.code || '').trim().toUpperCase();
  if (/GD\d+$/.test(code)) return 'Docentes';
  if (/G\d+$/.test(code)) return 'Estudiantes';

  return '';
}

function resolveAdvisoryType(type) {
  const normalizedType = String(type || '').trim().toUpperCase();
  return COP_ADVISORY_TYPE_LABELS[normalizedType] || String(type || '').trim();
}

function getSchoolDisplayName(row) {
  return String(row?.name ?? row?.address ?? '').trim();
}

function getRowKey(row) {
  return String(row?.id ?? row?.code ?? row?.name ?? row?.address ?? '').trim();
}

function getRecordGroupId(record) {
  return toNumber(record?.groupId ?? record?.group_id ?? record?.group?.id);
}

function getMeetingGroupId(record) {
  return toNumber(record?.id ?? record?.meetingGroupId ?? record?.meeting_group_id);
}

function getMeetingDateCandidate(record) {
  return (
    record?.meetingDate ??
    record?.meeting_date ??
    record?.date ??
    record?.attendanceDate ??
    record?.attendance_date ??
    ''
  );
}

function buildProcessRows(rows, isCopScope = false) {
  const normalizedRows = Array.isArray(rows) ? rows : [];

  if (isCopScope) {
    return Array.from(
      normalizedRows
        .reduce((rowsMap, row) => {
          const key = getRowKey(row);
          if (!key || rowsMap.has(key)) return rowsMap;
          rowsMap.set(key, row);
          return rowsMap;
        }, new Map())
        .values()
    )
      .map(row => ({
        'CODIGO ASESORIA': String(row?.code || '').trim(),
        'TIPO DE ASESORIA':
          resolveAdvisoryType(row?.type) ||
          String(row?.formativeProcessType || '').trim() ||
          resolveFormativeProcessType(row?.groupInfo),
        MEDIADOR: getUserDisplayName(row?.user),
        'MEDIADORES ADICIONALES': getAdditionalMediators(row),
        COMUNA: String(row?.municipality?.name || '').trim(),
        BARRIO: String(row?.neighborhood?.name || '').trim(),
        DEPENDENCIA: String(row?.organization || '').trim(),
        'NOMBRE ASESORIA': getSchoolDisplayName(row)
      }))
      .sort((left, right) =>
        String(left['CODIGO ASESORIA'] || '').localeCompare(
          String(right['CODIGO ASESORIA'] || ''),
          'es',
          { numeric: true, sensitivity: 'base' }
        )
      );
  }

  return normalizedRows
    .map(row => {
      const group = row?.groupInfo ?? null;
      const baseRow = {
        'CODIGO PROCESO FORMATIVO': String(row?.code || '').trim(),
        MEDIADOR: getUserDisplayName(row?.user),
        COMUNA: String(row?.municipality?.name || '').trim(),
        BARRIO: String(row?.neighborhood?.name || '').trim(),
        'ORGANIZACION MADRINA': String(row?.organization || '').trim(),
        'NOMBRE INSTITUCION EDUCATIVA': getSchoolDisplayName(row)
      };

      if (!group) {
        return {
          ...baseRow,
          'TIPO DE PROCESO FORMATIVO': String(row?.formativeProcessType || '').trim(),
          GRUPO: '',
          ESTADO: ''
        };
      }

      return {
        ...baseRow,
        'TIPO DE PROCESO FORMATIVO':
          String(row?.formativeProcessType || '').trim() ||
          resolveFormativeProcessType(group),
        GRUPO: String(group?.code || '').trim(),
        ESTADO: resolveGroupStatus(group, '', {
          schoolCode: row?.code
        }),
        'FECHA DE FINALIZACION': group?.finalizedAt || group?.finalized_at
          ? formatDateInBogota(group?.finalizedAt ?? group?.finalized_at)
          : '',
        MEDIADOR: getUserDisplayName(group?.user) || baseRow.MEDIADOR,
        COMUNA: String(group?.municipality?.name || '').trim() || baseRow.COMUNA,
        BARRIO: String(group?.neighborhood?.name || '').trim() || baseRow.BARRIO,
        'ORGANIZACION MADRINA':
          String(group?.organization || '').trim() || baseRow['ORGANIZACION MADRINA']
      };
    })
    .sort((left, right) => {
      const schoolCompare = String(left['CODIGO PROCESO FORMATIVO'] || '').localeCompare(
        String(right['CODIGO PROCESO FORMATIVO'] || ''),
        'es',
        { numeric: true, sensitivity: 'base' }
      );
      if (schoolCompare !== 0) return schoolCompare;

      return String(left.GRUPO || '').localeCompare(String(right.GRUPO || ''), 'es', {
        numeric: true,
        sensitivity: 'base'
      });
    });
}

function resolveAttendancesData(attendances) {
  const meetingGroups = Array.isArray(attendances?.meetingGroupsTable)
    ? attendances.meetingGroupsTable
    : Array.isArray(attendances?.meetings)
      ? attendances.meetings
      : [];
  const attendanceModels = Array.isArray(attendances?.attendancesModel)
    ? attendances.attendancesModel
    : [];

  return {
    meetingGroups,
    attendanceModels
  };
}

function buildAttendanceDateByMeetingGroup(attendanceModels) {
  return (Array.isArray(attendanceModels) ? attendanceModels : []).reduce(
    (attendanceDateByMeetingGroup, attendance) => {
      const meetingGroupId = toNumber(
        attendance?.meeting_group_id ??
          attendance?.meetingGroupId ??
          attendance?.meetingGroup?.id
      );
      const attendanceDate = formatIsoDateInBogota(getMeetingDateCandidate(attendance));

      if (!meetingGroupId || !attendanceDate) {
        return attendanceDateByMeetingGroup;
      }

      const currentDate = attendanceDateByMeetingGroup.get(meetingGroupId);
      if (!currentDate || attendanceDate < currentDate) {
        attendanceDateByMeetingGroup.set(meetingGroupId, attendanceDate);
      }

      return attendanceDateByMeetingGroup;
    },
    new Map()
  );
}

function buildFirstSessionByGroup(attendances) {
  const { meetingGroups, attendanceModels } = resolveAttendancesData(attendances);
  const attendanceDateByMeetingGroup = buildAttendanceDateByMeetingGroup(
    attendanceModels
  );
  const firstSessionByGroup = new Map();

  meetingGroups.forEach(meetingGroup => {
    const groupId = getRecordGroupId(meetingGroup);
    const meetingType = normalizeMeetingType(meetingGroup?.type);
    const meetingOrder = toNumber(meetingGroup?.order);
    const meetingGroupId = getMeetingGroupId(meetingGroup);
    const meetingDate =
      formatIsoDateInBogota(getMeetingDateCandidate(meetingGroup)) ||
      attendanceDateByMeetingGroup.get(meetingGroupId) ||
      '';

    if (!groupId || meetingType !== 'MEETING' || !meetingDate) {
      return;
    }

    const currentSession = firstSessionByGroup.get(groupId);
    const currentOrder = toNumber(currentSession?.order);
    const hasCurrentOrder = Number.isInteger(currentOrder) && currentOrder > 0;
    const hasMeetingOrder = Number.isInteger(meetingOrder) && meetingOrder > 0;

    const shouldReplace = !currentSession ||
      (hasMeetingOrder && !hasCurrentOrder) ||
      (hasMeetingOrder && hasCurrentOrder && meetingOrder < currentOrder) ||
      (hasMeetingOrder && hasCurrentOrder && meetingOrder === currentOrder && meetingDate < currentSession.date) ||
      (!hasMeetingOrder && !hasCurrentOrder && meetingDate < currentSession.date);

    if (shouldReplace) {
      firstSessionByGroup.set(groupId, {
        date: meetingDate,
        order: meetingOrder
      });
    }
  });

  return firstSessionByGroup;
}

function buildFirstSessionRows(rows, firstSessionByGroup) {
  const normalizedRows = Array.isArray(rows) ? rows : [];

  return normalizedRows
    .filter(row => Boolean(row?.groupInfo))
    .map(row => {
      const group = row.groupInfo;
      const groupId = toNumber(group?.id);
      const firstSessionDate = groupId ? firstSessionByGroup.get(groupId)?.date : '';

      return {
        'CODIGO PROCESO FORMATIVO': String(row?.code || '').trim(),
        GRUPO: String(group?.code || '').trim(),
        'FECHA PRIMERA SESION':
          firstSessionDate || 'No se ha realizado primera sesion'
      };
    })
    .sort((left, right) => {
      const schoolCompare = String(left['CODIGO PROCESO FORMATIVO'] || '').localeCompare(
        String(right['CODIGO PROCESO FORMATIVO'] || ''),
        'es',
        { numeric: true, sensitivity: 'base' }
      );
      if (schoolCompare !== 0) return schoolCompare;

      return String(left.GRUPO || '').localeCompare(String(right.GRUPO || ''), 'es', {
        numeric: true,
        sensitivity: 'base'
      });
    });
}

function getActivityGroupCreatedAt(activityGroup) {
  return (
    activityGroup?.createdAt ??
    activityGroup?.created_at ??
    ''
  );
}

function formatReportMonth(value) {
  if (!value) return '';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  const month = new Intl.DateTimeFormat('es-CO', {
    month: 'long',
    timeZone: 'America/Bogota'
  }).format(date);

  return month ? `${month.charAt(0).toUpperCase()}${month.slice(1)}` : '';
}

function buildSocialControlCharacterizationRows(beneficiaries) {
  const rows = [];

  (Array.isArray(beneficiaries) ? beneficiaries : []).forEach(beneficiary => {
    const activityGroup =
      beneficiary?.activityGroup ?? beneficiary?.activity_group;
    if (!activityGroup) return;

    const organizationType = String(
      beneficiary?.observations ?? ''
    ).trim();
    const organizationName = String(
      beneficiary?.address ?? ''
    ).trim();

    rows.push({
      Comuna: String(activityGroup?.comuna ?? '').trim(),
      'Tipo de Organización': organizationType,
      'Nombre de la organización': organizationName,
      'Actividad en la que Participó': String(
        activityGroup?.activity?.name ?? ''
      ).trim(),
      'Fecha en la que participó': formatIsoDateInBogota(
        beneficiary?.createdAt ?? beneficiary?.created_at
      ),
      'Mes del reporte': formatReportMonth(
        getActivityGroupCreatedAt(activityGroup)
      )
    });
  });

  return rows.sort((left, right) => {
    for (const header of SOCIAL_CONTROL_ORGANIZATION_HEADERS) {
      const comparison = String(left[header] || '').localeCompare(
        String(right[header] || ''),
        'es',
        { numeric: true, sensitivity: 'base' }
      );
      if (comparison !== 0) return comparison;
    }
    return 0;
  });
}

function buildSchoolsByMunicipalityRows(rows, isCopScope = false) {
  const normalizedRows = Array.isArray(rows) ? rows : [];

  if (!isCopScope) {
    const schoolsByMunicipality = normalizedRows.reduce((schoolsMap, row) => {
      const schoolName = getSchoolDisplayName(row);
      const municipalityName =
        String(row?.municipality?.name || '').trim() ||
        String(row?.groupInfo?.municipality?.name || '').trim();
      const neighborhoodName =
        String(row?.neighborhood?.name || '').trim() ||
        String(row?.groupInfo?.neighborhood?.name || '').trim();
      const schoolKey = [
        normalizeText(municipalityName),
        normalizeText(schoolName) || normalizeText(row?.id ?? row?.code)
      ].join('||');

      if (!schoolsMap.has(schoolKey)) {
        schoolsMap.set(schoolKey, {
          COMUNA: municipalityName,
          'NOMBRE INSTITUCION EDUCATIVA': schoolName,
          BARRIO: neighborhoodName
        });
      }

      const currentSchool = schoolsMap.get(schoolKey);

      if (!currentSchool.COMUNA && municipalityName) {
        currentSchool.COMUNA = municipalityName;
      }

      if (!currentSchool.BARRIO && neighborhoodName) {
        currentSchool.BARRIO = neighborhoodName;
      }

      if (!currentSchool['NOMBRE INSTITUCION EDUCATIVA'] && schoolName) {
        currentSchool['NOMBRE INSTITUCION EDUCATIVA'] = schoolName;
      }

      return schoolsMap;
    }, new Map());

    return Array.from(schoolsByMunicipality.values()).sort((left, right) => {
      const municipalityCompare = String(left.COMUNA || '').localeCompare(
        String(right.COMUNA || ''),
        'es',
        {
          sensitivity: 'base',
          numeric: true
        }
      );
      if (municipalityCompare !== 0) return municipalityCompare;

      const schoolCompare = String(
        left['NOMBRE INSTITUCION EDUCATIVA'] || ''
      ).localeCompare(
        String(right['NOMBRE INSTITUCION EDUCATIVA'] || ''),
        'es',
        {
          sensitivity: 'base',
          numeric: true
        }
      );
      if (schoolCompare !== 0) return schoolCompare;

      return String(left.BARRIO || '').localeCompare(
        String(right.BARRIO || ''),
        'es',
        {
          sensitivity: 'base',
          numeric: true
        }
      );
    });
  }

  const schoolsByMunicipality = normalizedRows.reduce((schoolsMap, row) => {
    const schoolCode = String(row?.code || '').trim();
    const schoolName = getSchoolDisplayName(row);
    const municipalityName =
      String(row?.groupInfo?.municipality?.name || '').trim() ||
      String(row?.municipality?.name || '').trim();
    const neighborhoodName =
      String(row?.groupInfo?.neighborhood?.name || '').trim() ||
      String(row?.neighborhood?.name || '').trim();
    const organizationName =
      String(row?.groupInfo?.organization || '').trim() ||
      String(row?.organization || '').trim();
    const mediatorName =
      getUserDisplayName(row?.groupInfo?.user) || getUserDisplayName(row?.user);
    const additionalMediators = getAdditionalMediators(row);
    const processType = isCopScope
      ? resolveAdvisoryType(row?.type)
      : String(row?.formativeProcessType || '').trim() ||
        resolveFormativeProcessType(row?.groupInfo);
    const groupCode = String(row?.groupInfo?.code || '').trim();
    const schoolKey = [
      municipalityName,
      schoolCode,
      schoolName
    ].join('||');

    if (!schoolsMap.has(schoolKey)) {
      schoolsMap.set(schoolKey, {
        COMUNA: municipalityName,
        'CODIGO PROCESO FORMATIVO': schoolCode,
        'NOMBRE INSTITUCION EDUCATIVA': schoolName,
        BARRIO: neighborhoodName,
        'ORGANIZACION MADRINA': organizationName,
        MEDIADOR: mediatorName,
        additionalMediators,
        processTypes: new Set(),
        groups: new Set()
      });
    }

    const currentSchool = schoolsMap.get(schoolKey);

    if (!currentSchool.BARRIO && neighborhoodName) {
      currentSchool.BARRIO = neighborhoodName;
    }

    if (!currentSchool['ORGANIZACION MADRINA'] && organizationName) {
      currentSchool['ORGANIZACION MADRINA'] = organizationName;
    }

    if (!currentSchool.MEDIADOR && mediatorName) {
      currentSchool.MEDIADOR = mediatorName;
    }

    if (!currentSchool.additionalMediators && additionalMediators) {
      currentSchool.additionalMediators = additionalMediators;
    }

    if (processType) {
      currentSchool.processTypes.add(processType);
    }

    if (groupCode && !isGroupCancelled(row?.groupInfo)) {
      currentSchool.groups.add(groupCode);
    }

    return schoolsMap;
  }, new Map());

  return Array.from(schoolsByMunicipality.values())
    .map(row => {
      const processTypes = sortTextValues(Array.from(row.processTypes));
      const groups = sortTextValues(Array.from(row.groups));

      if (isCopScope) {
        return {
          COMUNA: row.COMUNA,
          'CODIGO ASESORIA': row['CODIGO PROCESO FORMATIVO'],
          'NOMBRE ASESORIA': row['NOMBRE INSTITUCION EDUCATIVA'],
          BARRIO: row.BARRIO,
          DEPENDENCIA: row['ORGANIZACION MADRINA'],
          MEDIADOR: row.MEDIADOR,
          'MEDIADORES ADICIONALES': row.additionalMediators,
          'TIPOS DE ASESORIA': processTypes.join(', ')
        };
      }

      return {
        COMUNA: row.COMUNA,
        'CODIGO PROCESO FORMATIVO': row['CODIGO PROCESO FORMATIVO'],
        'NOMBRE INSTITUCION EDUCATIVA': row['NOMBRE INSTITUCION EDUCATIVA'],
        BARRIO: row.BARRIO,
        'ORGANIZACION MADRINA': row['ORGANIZACION MADRINA'],
        MEDIADOR: row.MEDIADOR,
        'TIPOS DE PROCESO FORMATIVO': processTypes.join(', '),
        GRUPOS: groups.join(', '),
        'TOTAL GRUPOS': groups.length
      };
    })
    .sort((left, right) => {
      const municipalityCompare = String(left.COMUNA || '').localeCompare(
        String(right.COMUNA || ''),
        'es',
        {
          sensitivity: 'base',
          numeric: true
        }
      );
      if (municipalityCompare !== 0) return municipalityCompare;

      const leftCode = isCopScope
        ? left['CODIGO ASESORIA']
        : left['CODIGO PROCESO FORMATIVO'];
      const rightCode = isCopScope
        ? right['CODIGO ASESORIA']
        : right['CODIGO PROCESO FORMATIVO'];
      const schoolCompare = String(leftCode || '').localeCompare(
        String(rightCode || ''),
        'es',
        {
          sensitivity: 'base',
          numeric: true
        }
      );
      if (schoolCompare !== 0) return schoolCompare;

      const leftName = isCopScope
        ? left['NOMBRE ASESORIA']
        : left['NOMBRE INSTITUCION EDUCATIVA'];
      const rightName = isCopScope
        ? right['NOMBRE ASESORIA']
        : right['NOMBRE INSTITUCION EDUCATIVA'];
      return String(leftName || '').localeCompare(
        String(rightName || ''),
        'es',
        {
          sensitivity: 'base',
          numeric: true
        }
      );
    });
}

function createSheet(rows, headers, widths) {
  const worksheet = rows.length > 0
    ? XLSX.utils.json_to_sheet(rows, { header: headers })
    : XLSX.utils.aoa_to_sheet([headers]);

  worksheet['!cols'] = widths.map(width => ({ wch: width }));
  return worksheet;
}

function downloadWorkbook(workbook, fileName) {
  const workbookOutput = XLSX.write(workbook, {
    bookType: 'xlsx',
    type: 'array'
  });
  const blob = new Blob([workbookOutput], {
    type: 'application/octet-stream'
  });

  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
}

function DownloadSchoolsReport({
  rows,
  isCopScope = false,
  isSocialControlScope = false
}) {
  const [isDownloading, setIsDownloading] = useState(false);
  const processRows = useMemo(
    () => buildProcessRows(rows, isCopScope),
    [rows, isCopScope]
  );
  const schoolsByMunicipalityRows = useMemo(
    () => buildSchoolsByMunicipalityRows(rows, isCopScope),
    [rows, isCopScope]
  );
  const hasRows = processRows.length > 0;

  async function handleDownloadReport() {
    if (isDownloading || !hasRows) {
      return;
    }

    setIsDownloading(true);

    try {
      if (isCopScope) {
        const workbook = XLSX.utils.book_new();
        const advisorySheet = createSheet(
          processRows,
          ADVISORY_SHEET_HEADERS,
          [22, 24, 28, 30, 18, 22, 26, 32]
        );
        const advisoriesByMunicipalitySheet = createSheet(
          schoolsByMunicipalityRows,
          ADVISORIES_BY_MUNICIPALITY_HEADERS,
          [18, 22, 32, 22, 26, 28, 30, 26]
        );

        XLSX.utils.book_append_sheet(workbook, advisorySheet, 'Asesorías');
        XLSX.utils.book_append_sheet(
          workbook,
          advisoriesByMunicipalitySheet,
          'Asesorías por comuna'
        );

        downloadWorkbook(
          workbook,
          `asesorias_${getCurrentIsoDateInBogota()}.xlsx`
        );
        return;
      }

      if (isSocialControlScope) {
        const beneficiaries = await getAllBeneficiaries(false, 'cs');
        if (beneficiaries === null) return;

        const workbook = XLSX.utils.book_new();
        const processSheet = createSheet(
          processRows,
          PROCESS_SHEET_HEADERS_WITHOUT_FINALIZATION,
          [24, 28, 18, 18, 28, 18, 22, 26, 30]
        );
        const organizationRows =
          buildSocialControlCharacterizationRows(beneficiaries);
        const organizationsSheet = createSheet(
          organizationRows,
          SOCIAL_CONTROL_ORGANIZATION_HEADERS,
          [18, 30, 34, 36, 26, 22]
        );

        XLSX.utils.book_append_sheet(workbook, processSheet, 'Procesos formativos');
        XLSX.utils.book_append_sheet(
          workbook,
          organizationsSheet,
          'Caracterizados'
        );

        downloadWorkbook(
          workbook,
          `procesos_formativos_control_social_${getCurrentIsoDateInBogota()}.xlsx`
        );
        return;
      }

      const attendances = await getAllAttendances();

      if (attendances === null) {
        return;
      }

      const firstSessionByGroup = buildFirstSessionByGroup(attendances);
      const firstSessionRows = buildFirstSessionRows(rows, firstSessionByGroup);
      const workbook = XLSX.utils.book_new();

      const processSheet = createSheet(processRows, PROCESS_SHEET_HEADERS, [
        24, 28, 18, 18, 22, 28, 18, 22, 26, 30
      ]);
      const firstSessionSheet = createSheet(
        firstSessionRows,
        FIRST_SESSION_HEADERS,
        [24, 18, 28]
      );
      const schoolsByMunicipalitySheet = createSheet(
        schoolsByMunicipalityRows,
        SCHOOLS_BY_MUNICIPALITY_HEADERS,
        [18, 30, 22]
      );

      XLSX.utils.book_append_sheet(workbook, processSheet, 'Procesos formativos');
      XLSX.utils.book_append_sheet(workbook, firstSessionSheet, 'Primeras sesiones');
      XLSX.utils.book_append_sheet(
        workbook,
        schoolsByMunicipalitySheet,
        'Schools por comuna'
      );

      downloadWorkbook(
        workbook,
        `procesos_formativos_${getCurrentIsoDateInBogota()}.xlsx`
      );
    } catch (error) {
      console.error(error);
      toast(
        isCopScope
          ? 'No se pudo generar el reporte de asesorías'
          : 'No se pudo generar el reporte de procesos formativos',
        {
        type: 'error'
        }
      );
    } finally {
      setIsDownloading(false);
    }
  }

  return (
    <button
      type='button'
      className='filters-clear-button download-schools-button'
      onClick={handleDownloadReport}
      disabled={isDownloading || !hasRows}
    >
      {isDownloading ? 'Descargando...' : 'Descargar excel'}
    </button>
  );
}

export default DownloadSchoolsReport;
